<script>
/**
 * O renderizador ÚNICO das partes de tool de um turno — [I-1] do review do
 * PR #2086, extraído de `chat-message.vue` (spec-001 [T011]).
 *
 * ★ Por que ele saiu da bolha do chat. As regras de "que cartão esta parte
 *   desenha" viviam dentro do `chat-message.vue`, e ele só é montado pelo
 *   `messages.vue` — que só o chat do CRM e o do parceiro usam. As duas
 *   superfícies do Fee Fixo (o drawer do Especialista e a conversa do Guia)
 *   montam o thread com markdown próprio, e por isso NUNCA desenhavam cartão
 *   nenhum: em `modo: 'oferecer'` (o default) ou com o guard reprovando, o
 *   `_tratarToolDeFront` faz o `upsertToolPart` e retorna sem `tool_output` —
 *   sem cartão, o consultor não via nem o botão nem o motivo, e o modelo não
 *   recebia aviso nenhum. Registrar o provedor de execução ali (o que já
 *   estava feito) só vale com o cartão desenhado.
 *
 * A moldura é a mesma nas três superfícies — `state`, `tool-call-id`, `label`,
 * `data-teste="parte-do-turno"`, `data-tool-call-id` e o canal `@respond` —
 * porque é ela que o contrato (`contracts/ui-components.md` §2), o E2E e os
 * cartões esperam. Cartão novo continua sendo uma entrada no `UI_REGISTRY`,
 * nunca uma linha aqui.
 *
 * ## Validação é assíncrona; recusa é visível
 *
 * `resolverEntrada` é síncrono (chave, flag e estado); `validarProps` é lazy —
 * o schema só sai do bundle quando um cartão daquele tipo aparece. Enquanto o
 * veredito não volta, o cartão é montado em `input-available` (o esqueleto que
 * todo cartão sabe desenhar), nunca com props não conferidos. Reprovou, ou a
 * chave não existe, ou o módulo não está liberado: entra o `fallback-card` com
 * o motivo. Dentro de uma conversa o pior desfecho não é o erro — é o silêncio.
 */
import { runDaConversa } from '@modules/aawz-intelligence-v2/utils/run-da-conversa'
import { renderizaPartesDoTurno } from './renderiza-partes'
import { descartarChavesIgnoradas } from './partes'

const DEFAULT_STORE_MODULE = 'aawz-intelligence-v2'
// O slot ÚNICO do run do Modo Agente — os canais do agente moram no store
// fixo, qualquer que seja a superfície (`apiChatModoAgenteEventos.js`).
const STORE_DO_RUN = 'aawz-intelligence-v2'

export default {
  name: 'IntelligenceV2PartesDoTurno',
  mixins: [renderizaPartesDoTurno],
  props: {
    /** As partes já filtradas pela superfície (`store/toolParts.js`). */
    partes: {
      type: Array,
      default: function() { return [] },
    },
    /**
     * A etapa da apresentação já é HISTÓRICO? Repassada à moldura, que a
     * entrega ao cartão de onboarding — é ela que decide se ele nasce aberto.
     *
     * ★ Aqui ela existe para a moldura ser IDÊNTICA à da bolha do chat (é o que
     * `partes-do-turno.unit.js` compara textualmente, e a regra está escrita em
     * `cartao-do-chat.js::CHAVES_DA_MOLDURA`: atributo novo no `<component>` da
     * bolha entra aqui no MESMO commit). As superfícies que montam ESTE
     * componente — as duas do Fee Fixo — não desenham onboarding, então o
     * default `false` as mantém byte a byte como estavam.
     */
    etapaHistorica: {
      type: Boolean,
      default: false,
    },
    /**
     * A fala com que a pessoa respondeu ao cartão de onboarding — repassada à
     * moldura pelo mesmo motivo da prop acima: paridade com a bolha do chat.
     * As superfícies que montam este componente não desenham onboarding; o
     * default vazio as mantém como estavam.
     */
    respostaDoCartao: {
      type: String,
      default: '',
    },
    /**
     * A ÚLTIMA mensagem do agente, quando a superfície quer que a conversa
     * REABERTA volte a mostrar o cartão.
     *
     * O slice `toolParts` é do TURNO: quando ele acaba, o `end` leva as partes
     * para o `tool_calls[]` da mensagem e o turno seguinte limpa o slice. Sem
     * esta prop, a superfície mostraria a ação enquanto o turno vive e a
     * perderia no reload — que é meio caminho do defeito que o [I-1] apontou.
     */
    ultimaMensagem: {
      type: Object,
      default: null,
    },
    /**
     * Namespace da superfície. NÃO vai à moldura de propósito: o namespace que
     * o cartão de ação usa para achar o motor de chat no clique já viaja dentro
     * de `ui.props` (`uiDaAcaoDeFront({namespace})`), e um binding individual
     * venceria o `v-bind="parte.props"` — trocando, no dia em que os dois
     * divergissem, o que o parser gravou pelo que a tela supôs.
     */
    storeModule: {
      type: String,
      default: DEFAULT_STORE_MODULE,
    },
    /**
     * As chaves que ESTA superfície já desenha por fora.
     *
     * Superfície que tem cartão próprio para uma chave passa a chave aqui, e a
     * parte é descartada — é o equivalente, por consumidor, do
     * `CHAVES_DE_GATE_LEGADO` que a bolha do chat aplica antes do mixin. As duas
     * superfícies do Fee Fixo mandam as seis do módulo
     * (`fee-fixo/ia/registry.js`), que elas desenham a partir da store.
     *
     * O descarte vale para as DUAS origens — as vivas do turno e as que voltam
     * no `tool_calls[]` da mensagem. Filtrar só as vivas mataria a duplicação
     * durante o streaming e a deixaria voltar no `end`, que é quando as partes
     * do turno passam para a mensagem (`apiChat._partesDoTurnoParaAMensagem`) e
     * o cartão da tela ainda está lá.
     */
    chavesIgnoradas: {
      type: Array,
      default: function() { return [] },
    },
  },
  computed: {
    /**
     * O turno ainda está correndo NESTA superfície ([P1] da corrida do T023).
     *
     * Vai à moldura porque é fato do turno, e o cartão que autoriza escrita
     * precisa dele para desabilitar os botões com o motivo à vista — a
     * superfície recusa a resposta enquanto o turno não termina, e clique mudo
     * é o defeito que isto fecha. A bolha do chat responde a mesma pergunta com
     * o `isStreaming` dela.
     */
    turnoEmVoo() {
      const store = this.$store
      if (!store) return false
      return Boolean(store.getters[this.storeModule + '/chatLoading'])
    },
    /**
     * O estado do run do Modo Agente DESTA conversa — para a moldura
     * (`estado-do-run`), o MESMO recorte que `messages.vue::runAtual` faz:
     * o run mora no slot único do store fixo e vale só para a conversa que
     * ele carrega (`utils/run-da-conversa.js`). Vazio = sem run aqui.
     *
     * ★ [review #2364] Existe aqui pela paridade da moldura com a bolha do
     * chat (`partes-do-turno.unit.js` compara os dois blocos textualmente):
     * o cartão de confirmação deixou de ler o slot cru do store, que com as
     * abas pintava a proposta da aba B com o run cancelado da aba A.
     */
    estadoDoRun() {
      const store = this.$store
      if (!store) return ''
      const run = store.getters[STORE_DO_RUN + '/runAtual'] || null
      const conversa = store.getters[this.storeModule + '/selectedConversation'] || null
      const daConversa = runDaConversa(run, conversa && conversa.id)
      return (daConversa && daConversa.estado) || ''
    },
    /** O contrato do mixin: as vivas do turno mais as que ficaram na mensagem. */
    partesParaRenderizar() {
      const todas = this.ultimaMensagem
        ? this.partesVivasEPersistidas(this.partes, this.ultimaMensagem)
        : this.partes
      return descartarChavesIgnoradas(todas, this.chavesIgnoradas)
    },
  },
  methods: {
    /**
     * O "Continuar" da apresentação. Aqui ele só SOBE — quem decide o que
     * fazer é a superfície (na bolha do chat, revelar a etapa e pedir a
     * rolagem). Existe pelo mesmo motivo da prop `etapaHistorica`: manter a
     * moldura idêntica à da bolha.
     */
    avancarNaApresentacao() {
      this.$emit('avancar')
    },
    // O canal genérico do contrato (`@respond({toolCallId, acao, payload})`). A
    // parte viaja junto porque quem trata lá em cima precisa saber de qual
    // cartão veio sem ter que reabrir o turno.
    onRespond(parte, evento) {
      this.$emit('respond', { parte: parte, ...evento })
    },
    onOpenService(payload) {
      this.$emit('open-service', payload)
    },
    onAsk(texto) {
      this.$emit('ask', texto)
    },
    /**
     * Clique no card de prévia do painel de artefato (Fase 2b, plano
     * `moonlit-conjuring-sun`) — mesma lógica de `chat-message.vue`, mantida
     * aqui só para a moldura dos dois renderers continuar IDÊNTICA (ver o
     * teste "a moldura do chat-message é idêntica à deste renderer" logo
     * abaixo). Nunca dispara de verdade nestas duas superfícies: o getter
     * `<storeModule>/modoArtefatoAtivo` só existe no namespace
     * `aawz-intelligence-v2`, e nem o drawer do Especialista nem o Guia
     * montam o card de prévia (`chavesIgnoradas` cobre os seis cartões do
     * módulo antes de chegar aqui).
     */
    onAbrirArtefato(parte) {
      if (!parte || !parte.parteReal) return
      const store = this.$store
      if (!store || typeof store.dispatch !== 'function') return
      store.dispatch(this.storeModule + '/abrirArtefato', parte.parteReal)
    },
    /**
     * Espelho de `onFecharArtefato` do `chat-message.vue` — mesma paridade de
     * moldura do `onAbrirArtefato` acima. Nas duas superfícies do Fee Fixo o
     * dispatch nunca dispara de verdade: a ação `fecharArtefato` só existe no
     * namespace `aawz-intelligence-v2`, e o card de prévia nem chega a montar
     * ali (`chavesIgnoradas` cobre os seis cartões do módulo antes daqui).
     */
    onFecharArtefato() {
      const store = this.$store
      if (!store || typeof store.dispatch !== 'function') return
      store.dispatch(this.storeModule + '/fecharArtefato')
    },
  },
}
</script>

<template>
  <!-- ★ [I-13] O espaçamento entre cartões vem do utilitário `az-gap` (16px),
       o MESMO que a bolha do chat aplica no laço dela: eram 12px de CSS local
       aqui contra nenhum lá, então dois cartões seguidos ficavam colados na
       bolha e afastados nas telas do Fee Fixo.

       O `v-if` substitui o antigo `.partes-do-turno:empty { display: none }`:
       o consumidor põe a margem na raiz (`mb-4`, `mt-3`), e sem partes ela
       deixaria um vão sozinho. Utilitário do Vuetify traz `!important`, que o
       `:empty` não venceria — não renderizar resolve os dois de uma vez. -->
  <!-- [11/09/2026] Mesma entrada animada da bolha do chat (`iv2-parte-*`,
       `design/efeitos.sass`): o cartão entra quando a tool responde, o
       invólucro `.iv2-parte` anima e carrega o crossfade da validação; a
       moldura do `<component>` segue idêntica nas duas superfícies. -->
  <transition-group
    v-if="partesResolvidas.length"
    name="iv2-parte"
    tag="div"
    class="d-flex flex-column az-gap"
  >
    <div
      v-for="parte in partesResolvidas"
      :key="parte.identidade"
      class="iv2-parte"
      :class="{ 'iv2-parte--validando': parte.validando }"
    >
      <component
        :is="parte.componente"
        v-bind="parte.props"
        :state="parte.state"
        :tool-call-id="parte.toolCallId"
        :id-proposta="parte.idProposta || null"
        :label="parte.label"
        data-teste="parte-do-turno"
        :data-tool-call-id="parte.toolCallId || null"
        :turno-em-voo="turnoEmVoo"
        :somente-leitura="parte.somenteLeitura === true"
        :etapa-historica="etapaHistorica"
        :resposta-do-cartao="respostaDoCartao"
        :estado-do-run="estadoDoRun"
        @respond="onRespond(parte, $event)"
        @avancar="avancarNaApresentacao"
        @open-service="onOpenService"
        @ask="onAsk"
        @abrir="onAbrirArtefato(parte)"
        @fechar="onFecharArtefato"
      />
    </div>
  </transition-group>
</template>

<style lang="sass" scoped>
// As classes de entrada dos cartões (`iv2-parte-*`) — ver o cabeçalho de
// `design/efeitos.sass`; o `@import` as escopa a este componente (§12).
@import '@modules/aawz-intelligence-v2/design/efeitos.sass'
</style>
